import { matchPath, useLocation } from 'react-router';

import AnnouncementIcon from '@mui/icons-material/AnnouncementOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import DashboardIcon from '@mui/icons-material/SpaceDashboardOutlined';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooksOutlined';
import MailIcon from '@mui/icons-material/MailOutlined';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import PeopleIcon from '@mui/icons-material/PeopleOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import ContactPageOutlinedIcon from '@mui/icons-material/ContactPageOutlined';

import { MenuItem, MenuList } from '@openclassrooms/ui-web/navigation';
import Typography from '@classify/react-core-components/Typography';
import { SelectableItemList } from '@openclassrooms/ui-web/ui';

import { useOnboardingsPermissions } from '@openclassrooms/data-connect-web/onboardings';
import { mergeRoutes, ROUTES, getPathName, getButtonLinkProps } from '@openclassrooms/routing';
import { translate } from '@openclassrooms/translations';
import { ImageCdn } from '@openclassrooms/images-cdn';

import { Paper } from '@mui/material';
import Box from '@classify/react-core-components/Box';
import Divider from '@classify/react-core-components/Divider';
import ProfileCompletion from '@classify/react-product-components/ProfileCompletion';
import { makeStyles } from '@mui/styles';

import {
  useUserExpectedBenefits,
  useCurrentUserContext,
} from '@openclassrooms/data-connect-web/users';

import hasHireExpectedBenefits from '../../services/hasHireExpectedBenefits';

import tutorIcon from './images/tutorIcon.svg';
import PromotionMenuItem from './PromotionMenuItem';

const DISPLAYABLE_NAME_MAX_LENGTH = 30;

const PATH = {
  LOGIN: '/login',
  SIGN_UP: '/register',
  DASHBOARD: '/dashboard',
  CREATE_COURSE: '/my-courses',
  ADMINISTRATION: '/spaces/admin/dashboard',
  MESSAGES: '/mp',
  ALERTS: '/alertes',
  NOTIFICATIONS: '/notifications',
  REFER_FRIEND: '/referral',
  MENTOR_DASHBOARD: '/mentorship/dashboard',
  TUTOR_DASHBOARD: '/tutor/dashboard',
};

const useStyles = makeStyles(
  ({ spacing, palette }) => ({
    profileAvatar: {
      marginRight: spacing(2),
    },
    menuItem: {
      minWidth: 304,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    divider: {
      '&&': {
        margin: 0,
      },
    },
    list: {
      outline: 0,
      paddingBottom: 0,
    },
    beta: {
      marginLeft: spacing(1),
      marginRight: spacing(0.5),
    },
    sectionTitle: {
      margin: spacing(1, 1, 0, 2),
      display: 'inline-block',
      color: palette.text.secondary,
    },
  }),
  { classNamePrefix: 'MainHeaderMenu' }
);

function MainHeaderMenu({
  displayableName = '',
  notificationCount = 0,
  moderationNotificationCount = 0,
  messageCount = 0,
  canSeeMentorDashboard = false,
  canSeeAdminDashboard = false,
  canSeeOrganizationDashboard = false,
  canSeeTutorDashboard = false,
  profileLink = '',
  profileCompletionRate = null,
  profileAvatar = <PersonIcon />,
  employerSpaceLink = '',
  canDisplayForumAlert = false,
  organizationName = '',
  onLogOut = () => {},
  ...otherProps
}) {
  const classes = useStyles();
  const location = useLocation();
  const { userId, clear: clearCurrentUserCache } = useCurrentUserContext();

  const { can: canOnOnboarding } = useOnboardingsPermissions();

  const canCompleteOrganizationOnboarding = canOnOnboarding(
    'COMPLETE_ONBOARDING',
    'B2B_CREATE_ORGANIZATION'
  );

  const { data: userExpectedBenefits } = useUserExpectedBenefits(
    { userId },
    {
      enabled: Boolean(userId),
    }
  );

  const isHiring = hasHireExpectedBenefits(userExpectedBenefits);

  const shouldSeeEmployerPlaceholderEntry =
    canCompleteOrganizationOnboarding &&
    !organizationName &&
    !canSeeOrganizationDashboard &&
    !canSeeAdminDashboard &&
    !isHiring;

  const menuItemProps = {
    component: 'a',
    className: classes.menuItem,
    role: 'link',
    tabIndex: 0,
  };

  const dividerProps = {
    role: 'separator',
    className: classes.divider,
  };

  const dashboardItems = [
    {
      condition: canSeeMentorDashboard,
      menuItem: {
        label: translate('main_header.support.dashboard'),
        value: 'mentor',
        leftIcon: <PeopleIcon />,
        'data-event': `Navigation|Clicked|Mentor::${userId}`,
        'data-testid': 'menuMentorLink',
        ...getButtonLinkProps(ROUTES.MENTORSHIP.DASHBOARD.INDEX),
      },
      pathsWhereSelected: mergeRoutes(ROUTES.MENTORSHIP),
    },
    {
      condition: canSeeTutorDashboard,
      menuItem: {
        label: translate('main_header.apprenticeship_manager.dashboard'),
        value: 'tutor',
        leftIcon: <ImageCdn src={tutorIcon} alt="" />,
        'data-event': `Navigation|Clicked|Tutor::${userId}`,
        'data-testid': 'menuTutorLink',
        ...getButtonLinkProps(ROUTES.TUTOR.DASHBOARD.INDEX),
      },
      pathsWhereSelected: mergeRoutes(ROUTES.TUTOR.DASHBOARD),
    },
    {
      condition: canSeeOrganizationDashboard && Boolean(employerSpaceLink),
      menuItem: {
        label: organizationName,
        value: 'organization',
        leftIcon: <BusinessIcon />,
        'data-testid': 'menuOrganizationLink',
        ...getButtonLinkProps(employerSpaceLink),
      },
      pathsWhereSelected: mergeRoutes(ROUTES.ORGANIZATIONS).concat(mergeRoutes(ROUTES.TEAMS)),
    },
    {
      condition: true,
      menuItem: {
        label: translate('main_header.student.dashboard'),
        value: 'student',
        leftIcon: <PersonIcon />,
        'data-testid': 'menuStudentLink',
        ...getButtonLinkProps(ROUTES.DASHBOARD.INDEX),
      },
      pathsWhereSelected: mergeRoutes(ROUTES.DASHBOARD),
    },
    {
      condition: canSeeAdminDashboard,
      menuItem: {
        label: translate('main_header.admin.dashboard'),
        value: 'admin',
        leftIcon: <DashboardIcon />,
        href: PATH.ADMINISTRATION,
        'data-testid': 'menuAdministrationLink',
      },
      pathsWhereSelected: mergeRoutes(ROUTES.ADMIN),
    },
    {
      condition: shouldSeeEmployerPlaceholderEntry,
      menuItem: {
        label: translate('main_header.employer.dashboard'),
        value: 'create-organization',
        leftIcon: <BusinessIcon />,
        href: getPathName(ROUTES.MY_ORGANIZATION.OVERVIEW),
        'data-testid': 'menuCreateOrganizationLink',
        'data-event': 'Navigation|Redirection::Internal|Menu drawer employers',
      },
      pathsWhereSelected: [],
    },
  ]
    .filter(({ condition }) => condition)
    .map(({ menuItem, pathsWhereSelected }) => ({
      variant: 'body',
      isSelected: pathsWhereSelected.some((path) =>
        Boolean(matchPath({ path }, location.pathname))
      ),
      ...menuItem,
    }));

  const hasOnlyOneDashboard = dashboardItems.length === 1;
  const hasSeveralDashboards = !hasOnlyOneDashboard && dashboardItems.length > 1;

  if (hasOnlyOneDashboard) {
    dashboardItems[0].label = translate('main_header.only_one_dashboard.dashboard');
    dashboardItems[0].leftIcon = <DashboardIcon />;
  }

  const profileAvatarIcon = <div className={classes.profileAvatar}>{profileAvatar}</div>;

  return (
    <Paper
      elevation={8}
      sx={{
        minWidth: 360,
        maxWidth: { sm: 360 },
        maxHeight: 'calc(100vh - 64px)',
        overflowY: 'auto',
      }}
    >
      <MenuList className={classes.list} role="menu" tabIndex={0} component="nav" {...otherProps}>
        <MenuItem
          {...menuItemProps}
          href={profileLink}
          label={
            <strong>
              {displayableName.length > DISPLAYABLE_NAME_MAX_LENGTH
                ? `${displayableName.substring(0, DISPLAYABLE_NAME_MAX_LENGTH)}...`
                : displayableName}
            </strong>
          }
          icon={profileAvatarIcon}
          sx={{ mb: 1 }}
        />
        <Divider {...dividerProps} />
        <div className={classes.menuItem}>
          {hasSeveralDashboards && (
            <Typography className={classes.sectionTitle} variant="overline">
              {translate('main_header.dashboard')}
            </Typography>
          )}
          <SelectableItemList
            listComponent="div"
            itemComponent="a"
            items={dashboardItems.map(({ pathsWhereSelected, ...item }) => item)}
            canClickOnSelected
          />
          <PromotionMenuItem />
        </div>
        <Divider {...dividerProps} />

        <div className={classes.menuItem}>
          <SelectableItemList
            listComponent="div"
            itemComponent="a"
            data-event="Navigation|Redirection::Internal|Menu logged access user profil'"
            items={[
              {
                label: translate('main_header.profile'),
                value: 'profile',
                leftIcon: <ContactPageOutlinedIcon />,
                href: getPathName(ROUTES.PROFILE.VIEW_AS, { persona: 'recruiter' }),
                isSelected: Boolean(matchPath({ path: ROUTES.PROFILE.VIEW_AS }, location.pathname)),
                variant: 'body',
                'aria-label': translate('main_header.profile'),
                labelAdornment: Number(profileCompletionRate) < 1 && (
                  <Box sx={{ ml: 1, display: 'flex', flexGrow: 1 }}>
                    <ProfileCompletion
                      completion={profileCompletionRate}
                      translations={{
                        label: '',
                      }}
                      variant="inline"
                    />
                  </Box>
                ),
              },
            ]}
            canClickOnSelected
          />

          <SelectableItemList
            listComponent="div"
            itemComponent="a"
            items={[
              {
                label: translate('main_header.account-settings'),
                value: 'account',
                leftIcon: <SettingsIcon />,
                href: getPathName(ROUTES.ACCOUNT.INDEX),
                isSelected: Boolean(matchPath({ path: ROUTES.ACCOUNT.INDEX }, location.pathname)),
                variant: 'body',
                'aria-label': translate('main_header.account-settings'),
              },
            ]}
            canClickOnSelected
          />

          <Divider {...dividerProps} />

          <SelectableItemList
            listComponent="div"
            itemComponent="a"
            items={[
              {
                label: translate('main_header.create_course'),
                value: 'my-courses',
                leftIcon: <LibraryBooksIcon />,
                href: PATH.CREATE_COURSE,
                isSelected: Boolean(matchPath({ path: PATH.CREATE_COURSE }, location.pathname)),
                variant: 'body',
              },
            ]}
            canClickOnSelected
          />

          <Divider {...dividerProps} />

          <SelectableItemList
            listComponent="div"
            itemComponent="a"
            items={[
              canDisplayForumAlert && {
                label: translate(
                  'main_header.moderation_notifications',
                  { count: moderationNotificationCount },
                  moderationNotificationCount
                ),
                value: 'moderation-notifications',
                leftIcon: <AnnouncementIcon />,
                href: PATH.ALERTS,
                isSelected: Boolean(matchPath({ path: PATH.ALERTS }, location.pathname)),
                variant: 'body',
              },
              {
                label: translate('main_header.messages', { count: messageCount }, messageCount),
                value: 'messages',
                leftIcon: <MailIcon />,
                href: PATH.MESSAGES,
                isSelected: Boolean(matchPath({ path: PATH.MESSAGES }, location.pathname)),
                variant: 'body',
              },
              {
                label: translate(
                  'main_header.notifications',
                  { count: notificationCount },
                  notificationCount
                ),
                value: 'notifications',
                leftIcon: <NotificationsIcon />,
                href: PATH.NOTIFICATIONS,
                isSelected: Boolean(matchPath({ path: PATH.NOTIFICATIONS }, location.pathname)),
                variant: 'body',
              },
            ]}
            canClickOnSelected
          />

          <Divider className={classes.divider} />
          <SelectableItemList
            listComponent="div"
            itemComponent="a"
            items={[
              {
                label: translate('main_header.deconnection'),
                value: 'disconnection',
                leftIcon: <PowerSettingsNewIcon />,
                onClick: async (e) => {
                  e.preventDefault();
                  await clearCurrentUserCache();
                  onLogOut();
                },
                variant: 'body',
              },
            ]}
            canClickOnSelected
          />
        </div>
      </MenuList>
    </Paper>
  );
}

export default MainHeaderMenu;
